import React, { FC, useEffect } from 'react';

import { PharmacistLegendVariant } from '@pharmaplan/common';
import { Box, Grid } from '@mui/material';
import { DateTime } from 'luxon';

import { nameTypes } from '../SelfService/Workshifts/WorkshiftForm/ViewEditContract/ViewEditContractTime/ViewEditContractTime';
import DateBox from '../SelfService/Workshifts/WorkshiftForm/ViewEditContract/ViewEditContractTime/DateBox';
import useViewAllLongTermContracts from '../../hooks/ViewAllLongTermContracts/useViewAllLongTermContracts';
import useLongTermFormikDetails from '../../hooks/LongTermContract/useLongTermFormikDetails';
import LongTermModalLayout from '../common/LongTermModalLayout';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { resetDialog } from '../../reducers/dialogReducer';
import LongContractCard from '../common/LongContractCard';
import { getDayMonthDateYear } from '../Reports/helpers';
import CustomCheckbox from '../common/CustomCheckbox';
import CustomChip from '../common/CustomChip';
import strings from '../../localization';
import useMultiBookingActions, {
  MultiBookingActionType,
} from '../../hooks/MultiBookingActionScreen/useMultiBookingActions';

import styles from './styles';

const { start, end } = nameTypes;

interface IMultiBookingActionScreen {
  type: MultiBookingActionType;
  longTermContractId: string;
  professionalId?: string;
}

const today = DateTime.now().toISO({ includeOffset: false }) ?? '';

const MultiBookingActionScreen: FC<IMultiBookingActionScreen> = ({
  longTermContractId,
  professionalId,
  type,
}) => {
  const dispatch = useAppDispatch();

  const { getAPI, contract, userData, longTermContract, successAction } = useViewAllLongTermContracts({
    longTermContractId,
    type: PharmacistLegendVariant.booked,
    professionalId,
  });

  const { data } = contract ?? {};

  const {
    handleCheckboxClick,
    handleSelectAll,
    selectedIds,
    isAllSelected,
    handleSubmit,
    label,
  } = useMultiBookingActions(data, type);

  const { formik } = useLongTermFormikDetails(data, longTermContract, '');

  const filteredData = type === MultiBookingActionType.MarkAsPaid
    ? data.filter((item) =>
      item.startDate < today)
    : data.filter((item) =>
      item.startDate > today);

  useEffect(() => {
    getAPI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    dispatch(resetDialog());
  };

  return (
    <LongTermModalLayout
      primary={{
        label,
        onPress: handleSubmit,
        disabled:
          !selectedIds?.length && type === MultiBookingActionType.CancelBooking,
      }}
      secondary={{ label: strings.close, onPress: handleClose }}
    >
      <Grid md={5} xs={12} item>
        <LongContractCard
          formik={formik}
          event={userData}
          isBooked
          successAction={successAction}
        />
      </Grid>
      <Grid md={7} xs={12} item>
        <Box>
          <CustomCheckbox
            label={strings.selectAll}
            name="selectAll"
            handleChange={handleSelectAll}
            value={isAllSelected}
            id="selectAll"
          />
        </Box>
        <Box sx={styles.dateBoxGrid}>
          {filteredData?.map?.((interval) => {
            const Chip = (
              <CustomChip
                type={PharmacistLegendVariant.booked}
                label={strings.booked}
              />
            );

            const { startDate, bookingId = '' } = interval ?? {};
            const date = startDate?.split?.('T')?.[0];

            return (
              <DateBox
                Chip={Chip}
                key={date}
                checkboxValue={selectedIds?.includes(bookingId)}
                handleCheckboxClick={handleCheckboxClick(bookingId)}
                showCheckbox
                changedName={`${date}|${nameTypes.changed}`}
                title={`${getDayMonthDateYear(date)}`}
                startName={`${date}|${start}`}
                endName={`${date}|${end}`}
                formik={formik}
                hideDayOff
                disabled
                dayOffName=""
              />
            );
          })}
        </Box>
      </Grid>
    </LongTermModalLayout>
  );
};

export default MultiBookingActionScreen;
